import { toStringField } from 'framework/utils';
import {
  InterruptionContext,
  InterruptionDto,
  InterruptionEmployeeResponseStatus,
  InterruptionType,
  InterruptionWireDto,
} from '../../types';

const parseContextString = (value: string): InterruptionContext | null => {
  try {
    const parsed = JSON.parse(value) as Dictionary<unknown>;

    return {
      creatorEmail: toStringField(parsed.creatorEmail),
      creatorName: toStringField(parsed.creatorName),
      recipientEmail: toStringField(parsed.recipientEmail),
      recipientName: toStringField(parsed.recipientName),
      issueId: toStringField(parsed.issueId),
      source: toStringField(parsed.source),
    };
  } catch {
    return null;
  }
};

export const normalizeInterruptionContext = (context: InterruptionWireDto['context']): InterruptionDto['context'] => {
  if (typeof context === 'string') {
    return parseContextString(context);
  }

  return context;
};

export const normalizeInterruption = (interruption: InterruptionWireDto): InterruptionDto => ({
  ...interruption,
  context: normalizeInterruptionContext(interruption.context),
});

export const normalizeInterruptions = (interruptions: InterruptionWireDto[]) =>
  interruptions.map(normalizeInterruption);

export const isCurrentUserInterruptionRecipient = (interruption: InterruptionDto, currentUserId: string) => {
  return Boolean(currentUserId && interruption.employeeId === currentUserId);
};

export const isCurrentUserInterruptionCreator = (interruption: InterruptionDto, currentUserId: string) => {
  return Boolean(currentUserId && interruption.creatorId === currentUserId);
};

export const isActiveIncomingInterruption = (interruption: InterruptionDto, currentUserId: string) => {
  return (
    isCurrentUserInterruptionRecipient(interruption, currentUserId) &&
    !isCurrentUserInterruptionCreator(interruption, currentUserId) &&
    interruption.employeeResponseStatus === InterruptionEmployeeResponseStatus.New
  );
};

export const isEmergencyInterruptionForCurrentUser = (interruption: InterruptionDto, currentUserId: string) => {
  return (
    interruption.type === InterruptionType.OnCallEmergency &&
    (isCurrentUserInterruptionRecipient(interruption, currentUserId) ||
      isCurrentUserInterruptionCreator(interruption, currentUserId))
  );
};

export const isListInterruptionForCurrentUser = (interruption: InterruptionDto, currentUserId: string) => {
  return (
    interruption.type === InterruptionType.TaskStatusPing && isActiveIncomingInterruption(interruption, currentUserId)
  );
};
